import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, createBlock as _createBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "app-data-grid" }
const _hoisted_2 = {
  key: 0,
  class: "search-container"
}
const _hoisted_3 = { class: "search-input-wrapper" }
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = {
  key: 2,
  class: "mobile-card-view"
}
const _hoisted_6 = {
  key: 0,
  class: "mobile-empty-state"
}
const _hoisted_7 = { class: "mobile-empty-text" }
const _hoisted_8 = {
  key: 1,
  class: "mobile-cards"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "mobile-card__header" }
const _hoisted_11 = {
  class: "mobile-card__avatar",
  "aria-hidden": "true"
}
const _hoisted_12 = { class: "mobile-card__info" }
const _hoisted_13 = { class: "mobile-card__title" }
const _hoisted_14 = {
  key: 0,
  class: "mobile-card__subtitle"
}
const _hoisted_15 = ["title", "aria-label", "onClick"]
const _hoisted_16 = {
  key: 0,
  class: "mobile-card__details"
}
const _hoisted_17 = { class: "mobile-card__detail-label" }
const _hoisted_18 = { class: "mobile-card__detail-value" }
const _hoisted_19 = {
  key: 2,
  class: "mobile-pagination"
}
const _hoisted_20 = ["disabled"]
const _hoisted_21 = { class: "mobile-pagination__info" }
const _hoisted_22 = ["disabled"]

import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { AgGridVue } from 'ag-grid-vue3';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Types
interface ColumnDef {
  field?: string;
  headerName?: string;
  type?: string;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  resizable?: boolean;
  sortable?: boolean;
  filter?: boolean | string;
  cellRenderer?: string | Function;
  cellStyle?: any;
  valueFormatter?: Function;
  valueGetter?: Function;
  [key: string]: any;
}

interface Action {
  name: string;
  label: string;
  icon?: string;
  color?: string;
  class?: string;
  condition?: Function;
  handler: Function;
}

interface GridEvent {
  api: any;
  columnApi: any;
  type: string;
}

interface RowEvent {
  data: any;
  rowIndex: number;
  api: any;
  columnApi: any;
}

interface PaginationEvent {
  page: number;
  pageSize: number;
  totalPages: number;
  totalRows: number;
}

// Props
interface Props {
  rowData: any[];
  columnDefs: ColumnDef[];
  pagination?: boolean;
  pageSize?: number;
  pageSizeOptions?: number[];
  suppressPaginationPanel?: boolean;
  animateRows?: boolean;
  suppressMovableColumns?: boolean;
  domLayout?: string;
  gridClass?: string;
  gridStyle?: string | object;
  actions?: Action[];
  defaultColDef?: Partial<ColumnDef>;
  showSearch?: boolean;
  searchPlaceholder?: string;
}

interface Emits {
  'grid-ready': [event: GridEvent];
  'pagination-changed': [event: PaginationEvent];
  'selection-changed': [event: any[]];
  'row-clicked': [event: RowEvent];
  'action-clicked': [action: Action, data: any];
}

const MOBILE_BREAKPOINT = 768;
interface MobileDetailField {
      key: string;
      label: string;
      colDef: ColumnDef;
    }

    
export default /*@__PURE__*/_defineComponent({
  __name: 'AppDataGrid',
  props: {
    rowData: {},
    columnDefs: {},
    pagination: { type: Boolean, default: true },
    pageSize: { default: 10 },
    pageSizeOptions: { default: () => [10, 20, 50, 100] },
    suppressPaginationPanel: { type: Boolean, default: true },
    animateRows: { type: Boolean, default: true },
    suppressMovableColumns: { type: Boolean, default: false },
    domLayout: { default: 'normal' },
    gridClass: { default: '' },
    gridStyle: { default: 'width: 100%; height: 100%;' },
    actions: { default: () => [] },
    defaultColDef: { default: () => ({}) },
    showSearch: { type: Boolean, default: true },
    searchPlaceholder: { default: '' }
  },
  emits: ["grid-ready", "pagination-changed", "selection-changed", "row-clicked", "action-clicked"],
  setup(__props: any, { emit: __emit }) {

/// <reference types="vue" />
// @ts-ignore
const props = __props;

// Emits
const emit = __emit;

// Composables
const { t } = useI18n();

// Refs
const gridRef = ref(null);
const searchQuery = ref('');
const currentPageSize = ref(props.pageSize);

// Mobile viewport detection
const isMobile = ref(typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
const mobilePage = ref(1);

// Watch for prop changes to update local page size
watch(() => props.pageSize, (newSize: number) => {
  currentPageSize.value = newSize;
});

// Reset mobile page when search query changes
watch(searchQuery, () => {
  mobilePage.value = 1;
});
    
    // Default column configuration
    const baseDefaultColDef = {
      resizable: true,
      sortable: true,
      wrapText: false,
      autoHeight: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center',
        lineHeight: '1.4'
      }
    };
    
    // Merge user-provided defaultColDef with base configuration
    const mergedDefaultColDef = computed(() => ({
      ...baseDefaultColDef,
      ...props.defaultColDef
    }));
    
    // Cell renderers for different data types
    const cellRenderers: Record<string, (params: any) => string> = {
      text: (params: any) => {
        if (params.value == null) return '';
        return `<span title="${params.value}">${params.value}</span>`;
      },
      
      boolean: (params: any) => {
        if (params.value == null) return '';
        const isTrue = params.value === true || params.value === 'true';
        return `
          <span class="boolean-cell ${isTrue ? 'boolean-true' : 'boolean-false'}">
            <i class="fas fa-${isTrue ? 'check' : 'times'}" aria-hidden="true"></i>
            ${isTrue ? t('common.yes') : t('common.no')}
          </span>
        `;
      },
        date: (params: any) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return `<span class="date-cell">${date.toLocaleDateString()}</span>`;
      },
      
      timestamp: (params: any) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        const dateStr = date.toLocaleDateString();
        const timeStr = date.toLocaleTimeString();
        return `<span class="timestamp-cell">${dateStr} ${timeStr}</span>`;
      },
      
      number: (params: any) => {
        if (params.value == null) return '';
        const value = Number(params.value);
        return `<span class="number-cell">${value.toLocaleString()}</span>`;
      },
      
      currency: (params: any) => {
        if (params.value == null) return '';
        const value = Number(params.value);
        return `<span class="currency-cell">${new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(value)}</span>`;
      }
    };
      // Actions cell renderer    
      const actionsRenderer = (params: any) => {
      if (!props.actions || props.actions.length === 0) return '';
      
      // Filter actions based on showCondition if provided
      const visibleActions = props.actions.filter((action: Action) => {
        if (!action.condition) return true;
        return action.condition(params.data);
      });
      
      if (visibleActions.length === 0) return '';
      
      const actionButtons = visibleActions.map((action: Action) => `
        <button 
          class="action-btn ${action.class || ''}" 
          data-action="${action.name}"
          title="${action.label || action.name}"
          aria-label="${action.label || action.name}"
        >
          <i class="${action.icon}" aria-hidden="true"></i>
        </button>
      `).join('');
      
      return `<div class="action-buttons" role="group" aria-label="Actions">${actionButtons}</div>`;
    };
      // Process column definitions to apply appropriate renderers
    const processedColumnDefs = computed(() => {
      const columns = [...props.columnDefs];
      
      // Apply cell renderers based on column type
      columns.forEach(column => {
        if (column.type && cellRenderers[column.type] && !column.cellRenderer) {
          column.cellRenderer = cellRenderers[column.type];
        }
        
        // Add appropriate CSS classes for different types
        if (column.type) {
          column.cellClass = column.cellClass || `${column.type}-cell`;
        }
        
        // Handle showCondition for columns by extending the cellRenderer
        if (column.showCondition) {
          const originalCellRenderer = column.cellRenderer;
          column.cellRenderer = (params: any) => {
            if (!column.showCondition(params.data)) {
              return '';
            }
            return originalCellRenderer ? originalCellRenderer(params) : params.value;
          };
        }
      });
      
      // Add actions column if actions are provided
      if (props.actions && props.actions.length > 0) {
        // Calculate dynamic width based on number of actions
        // Base width: 55px for header + padding
        // Per button: 44px (36px button + 8px gap)
        const numActions = props.actions.length;
        const baseWidth = 55;
        const buttonWidth = 44;
        const dynamicWidth = baseWidth + (numActions * buttonWidth);
        
        columns.push({
          headerName: t('common.actions'),
          field: 'actions',
          width: dynamicWidth,
          minWidth: dynamicWidth,
          maxWidth: dynamicWidth,
          resizable: false,
          sortable: false,
          filter: false,
          cellRenderer: actionsRenderer,
          pinned: 'right'
        });
      }
      
      return columns;
    });
    
    // Strip HTML tags from renderer output so the visible text can be searched.
    const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

    const getColumnRawValue = (row: any, column: ColumnDef): any => {
      if (typeof column.valueGetter === 'function') {
        try {
          return column.valueGetter({ data: row, colDef: column });
        } catch {
          // Fall back to the field value when a value getter needs grid-only params.
        }
      }

      return column.field ? row[column.field] : undefined;
    };

    // Include text produced by formatters/renderers. Translated labels often only
    // exist in this display layer while the underlying row contains an enum value.
    const getColumnSearchText = (row: any, column: ColumnDef): string => {
      const rawValue = getColumnRawValue(row, column);
      const values: string[] = rawValue == null ? [] : [String(rawValue)];
      let formattedValue: any;

      if (typeof column.valueFormatter === 'function') {
        try {
          formattedValue = column.valueFormatter({
            value: rawValue,
            data: row,
            colDef: column
          });
          if (formattedValue != null) values.push(String(formattedValue));
        } catch {
          // A formatter may rely on AG Grid params unavailable during global search.
        }
      }

      if (typeof column.cellRenderer === 'function') {
        try {
          const renderedValue = column.cellRenderer({
            value: rawValue,
            valueFormatted: formattedValue,
            data: row,
            colDef: column
          });
          if (typeof renderedValue === 'string') values.push(stripHtml(renderedValue));
        } catch {
          // A renderer may rely on AG Grid params unavailable during global search.
        }
      }

      return values.join(' ');
    };

    // Filtered row data based on raw values and the text visible in grid cells.
    const filteredRowData = computed(() => {
      if (!searchQuery.value) return props.rowData;
      
      const query = searchQuery.value.trim().toLocaleLowerCase();
      if (!query) return props.rowData;

      return props.rowData.filter((row: any) => {
        const rawValueMatches = Object.values(row).some(value =>
          String(value).toLocaleLowerCase().includes(query)
        );
        if (rawValueMatches) return true;

        return props.columnDefs.some((column: ColumnDef) =>
          getColumnSearchText(row, column).toLocaleLowerCase().includes(query)
        );
      });
    });
    
    // Event handlers
    const onGridReady = (params: any) => {
      // Auto-size columns to fit
      if (params.api) {
        setTimeout(() => {
          params.api.sizeColumnsToFit();
          
          // Add data-testid to rows after grid is ready
          addDataTestIdToRows(params.api);
        }, 100);
      }
      emit('grid-ready', params);
    };
    
    // Function to add data-testid attributes to grid rows
    const addDataTestIdToRows = (api: any) => {
      setTimeout(() => {
        const rowElements = document.querySelectorAll('.ag-row');
        rowElements.forEach((rowElement) => {
          // Add basic row testid
          rowElement.setAttribute('data-testid', 'project-row');
          
          // Try to get the row data to add more specific attributes
          const rowIndexStr = rowElement.getAttribute('row-index');
          const rowIndex = rowIndexStr ? parseInt(rowIndexStr) : null;
          if (rowIndex !== null && !isNaN(rowIndex) && api) {
            const rowNode = api.getDisplayedRowAtIndex(rowIndex);
            if (rowNode && rowNode.data) {
              // Add project name as data attribute for easier testing
              if (rowNode.data.name) {
                rowElement.setAttribute('data-project-name', rowNode.data.name);
              }
              // Add project id as data attribute
              if (rowNode.data.id) {
                rowElement.setAttribute('data-project-id', rowNode.data.id);
              }
            }
          }
        });
      }, 200);
    };
    
    const onPaginationChanged = (params: any) => {
      // Update local page size when user changes it in the grid
      const newPageSize = params.api.paginationGetPageSize();
      if (newPageSize !== currentPageSize.value) {
        currentPageSize.value = newPageSize;
      }
      
      // Add data-testid to new rows when pagination changes
      addDataTestIdToRows(params.api);
      emit('pagination-changed', params);
    };
    
    const onSelectionChanged = (params: any) => {
      emit('selection-changed', params);
    };
      const onRowClicked = (params: any) => {
      // Check if the click originated from an action button
      const actionElement = params.event?.target?.closest('[data-action]');
      if (actionElement) {
        // Don't emit row-clicked if an action button was clicked
        return;
      }
      emit('row-clicked', params);
    };
    
    const onCellClicked = (params: any) => {
      // Check if this is an action button click
      const actionElement = params.event.target.closest('[data-action]');
      if (actionElement) {
        const actionName = actionElement.getAttribute('data-action');
        const action = props.actions.find((a: Action) => a.name === actionName);
        if (action) {
          emit('action-clicked', action, params.data);
        }
        // Prevent row click event from firing
        params.event.stopPropagation();
      }
    };
    
    // Search input handlers
    const onSearchInput = (event: any) => {
      searchQuery.value = event.target.value;
    };
    
    const clearSearch = () => {
      searchQuery.value = '';
    };
    
    // Expose grid API methods
    const getGridApi = () => {
      return gridRef.value?.api;
    };
    
    const getGridColumnApi = () => {
      return gridRef.value?.columnApi;
    };
    
    const sizeColumnsToFit = () => {
      const api = getGridApi();
      if (api) {
        api.sizeColumnsToFit();
      }
    };
    
    const autoSizeAllColumns = () => {
      const api = getGridApi();
      if (api) {
        api.autoSizeAllColumns();
      }
    };
    
    // Handle window resize
    const handleResize = () => {
      if (typeof window === 'undefined') return;
      isMobile.value = window.innerWidth <= MOBILE_BREAKPOINT;
      if (!isMobile.value) {
        setTimeout(() => {
          sizeColumnsToFit();
        }, 100);
      }
    };

    // ── Mobile card view helpers ────────────────────────────────────────────

    // Column defs excluding the auto-added actions column and hidden columns
    const mobileColumns = computed(() =>
      props.columnDefs.filter(col => col.field && col.field !== 'actions' && !col.hide)
    );

    const mobileTitleField = computed(() => mobileColumns.value[0]?.field ?? '');
    const mobileSubtitleField = computed(() => mobileColumns.value[1]?.field ?? '');

    const mobileDetailFields = computed((): MobileDetailField[] =>
      mobileColumns.value.slice(2).map(col => ({
        key: col.field!,
        label: col.headerName ?? col.field!,
        colDef: col,
      }))
    );

    const mobilePagedData = computed(() => {
      if (!props.pagination) return filteredRowData.value;
      const start = (mobilePage.value - 1) * currentPageSize.value;
      return filteredRowData.value.slice(start, start + currentPageSize.value);
    });

    const mobileTotalPages = computed(() =>
      props.pagination
        ? Math.max(1, Math.ceil(filteredRowData.value.length / currentPageSize.value))
        : 1
    );

    // Resolve the display value for a cell using valueGetter → valueFormatter → cellRenderer → raw
    const getMobileFieldValue = (item: any, col: ColumnDef): string => {
      let rawValue: any;
      if (typeof col.valueGetter === 'function') {
        try { rawValue = col.valueGetter({ data: item, colDef: col }); } catch { /* fallback */ }
      }
      if (rawValue === undefined) rawValue = col.field ? item[col.field] : undefined;
      if (rawValue == null || rawValue === '') return '';

      if (typeof col.valueFormatter === 'function') {
        try {
          const formatted = col.valueFormatter({ value: rawValue, data: item, colDef: col });
          if (formatted != null) return String(formatted);
        } catch { /* fallback */ }
      }

      if (typeof col.cellRenderer === 'function') {
        try {
          const rendered = col.cellRenderer({ value: rawValue, data: item, colDef: col });
          if (typeof rendered === 'string') {
            return rendered.includes('<') ? stripHtml(rendered) : rendered;
          }
        } catch { /* fallback */ }
      }

      return String(rawValue);
    };

    const getMobileTitle = (item: any): string => {
      const col = mobileColumns.value[0];
      return col ? getMobileFieldValue(item, col) : '';
    };

    const getMobileSubtitle = (item: any): string => {
      const col = mobileColumns.value[1];
      return col ? getMobileFieldValue(item, col) : '';
    };

    const getMobileInitials = (item: any): string => {
      const title = getMobileTitle(item);
      const words = title.trim().split(/\s+/).filter((w: string) => w.length > 0);
      if (words.length >= 2) return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
      if (words[0]) return words[0].substring(0, 2).toUpperCase();
      return '??';
    };

    const getVisibleActionsForItem = (item: any): Action[] =>
      props.actions.filter(action => !action.condition || action.condition(item));

    const onMobileCardClick = (item: any): void => {
      emit('row-clicked', { data: item, rowIndex: -1, api: null, columnApi: null } as any);
    };

    const onMobileActionClick = (action: Action, item: any): void => {
      emit('action-clicked', action, item);
    };

    // ── End mobile helpers ──────────────────────────────────────────────────

    // Row configuration functions
    const getRowId = (params: any): string => {
      return String(params.data?.id ?? params.data?.name ?? params.node?.rowIndex ?? -1);
    };
    
    const rowClassRules = {
      'project-row': () => true // Add project-row class to all rows
    };
    
      onMounted(() => {
      window.addEventListener('resize', handleResize);
    });    
    onUnmounted(() => {
      window.removeEventListener('resize', handleResize);
    });
    
    // Watch for rowData changes to re-apply data-testid attributes
    watch(() => props.rowData, () => {
      mobilePage.value = 1;
      const api = getGridApi();
      if (api) {
        setTimeout(() => {
          addDataTestIdToRows(api);
        }, 300);
      }
    }, { deep: true });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_ctx.showSearch)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[5] || (_cache[5] = _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              width: "20",
              height: "20",
              class: "search-icon"
            }, [
              _createElementVNode("path", {
                fill: "currentColor",
                d: "M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
              })
            ], -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              class: "search-input",
              placeholder: _ctx.searchPlaceholder || _unref(t)('common.search'),
              onInput: onSearchInput
            }, null, 40, _hoisted_4), [
              [_vModelText, searchQuery.value]
            ]),
            (searchQuery.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  onClick: clearSearch,
                  class: "search-clear"
                }, [...(_cache[4] || (_cache[4] = [
                  _createElementVNode("svg", {
                    viewBox: "0 0 24 24",
                    width: "16",
                    height: "16"
                  }, [
                    _createElementVNode("path", {
                      fill: "currentColor",
                      d: "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z"
                    })
                  ], -1)
                ]))]))
              : _createCommentVNode("", true)
          ])
        ]))
      : _createCommentVNode("", true),
    _cache[9] || (_cache[9] = _createTextVNode()),
    (!isMobile.value)
      ? (_openBlock(), _createBlock(_unref(AgGridVue), _mergeProps({
          key: 1,
          ref_key: "gridRef",
          ref: gridRef,
          "data-testid": "projects-grid",
          class: ['ag-theme-alpine', _ctx.gridClass],
          columnDefs: processedColumnDefs.value,
          rowData: filteredRowData.value,
          defaultColDef: mergedDefaultColDef.value,
          pagination: _ctx.pagination,
          paginationPageSize: currentPageSize.value,
          paginationPageSizeSelector: _ctx.pageSizeOptions,
          suppressPaginationPanel: _ctx.suppressPaginationPanel,
          animateRows: _ctx.animateRows,
          getRowId: getRowId,
          rowClassRules: rowClassRules,
          onGridReady: onGridReady,
          onPaginationChanged: onPaginationChanged,
          onSelectionChanged: onSelectionChanged,
          onRowClicked: onRowClicked,
          onCellClicked: onCellClicked,
          suppressMovableColumns: _ctx.suppressMovableColumns,
          style: _ctx.gridStyle,
          domLayout: _ctx.domLayout
        }, _ctx.$attrs, { theme: "legacy" }), null, 16, ["class", "columnDefs", "rowData", "defaultColDef", "pagination", "paginationPageSize", "paginationPageSizeSelector", "suppressPaginationPanel", "animateRows", "suppressMovableColumns", "style", "domLayout"]))
      : (_openBlock(), _createElementBlock("div", _hoisted_5, [
          (filteredRowData.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _cache[6] || (_cache[6] = _createElementVNode("svg", {
                  viewBox: "0 0 24 24",
                  width: "48",
                  height: "48",
                  class: "mobile-empty-icon"
                }, [
                  _createElementVNode("path", {
                    fill: "currentColor",
                    d: "M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
                  })
                ], -1)),
                _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)('common.noData')), 1)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mobilePagedData.value, (item, mobileIdx) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: item.id ?? item.name ?? String(mobileIdx),
                    class: "mobile-card",
                    onClick: ($event: any) => (onMobileCardClick(item))
                  }, [
                    _createElementVNode("div", _hoisted_10, [
                      _createElementVNode("div", _hoisted_11, _toDisplayString(getMobileInitials(item)), 1),
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("div", _hoisted_13, _toDisplayString(getMobileTitle(item)), 1),
                        (mobileSubtitleField.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(getMobileSubtitle(item)), 1))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("div", {
                        class: "mobile-card__actions",
                        onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getVisibleActionsForItem(item), (action) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: action.name,
                            class: _normalizeClass(['mobile-action-btn', action.class]),
                            title: action.label,
                            "aria-label": action.label,
                            onClick: _withModifiers(($event: any) => (onMobileActionClick(action, item)), ["stop"])
                          }, [
                            (action.icon)
                              ? (_openBlock(), _createElementBlock("i", {
                                  key: 0,
                                  class: _normalizeClass(action.icon),
                                  "aria-hidden": "true"
                                }, null, 2))
                              : _createCommentVNode("", true)
                          ], 10, _hoisted_15))
                        }), 128))
                      ])
                    ]),
                    (mobileDetailFields.value.length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mobileDetailFields.value, (field) => {
                            return (_openBlock(), _createElementBlock("div", {
                              key: field.key,
                              class: "mobile-card__detail-item"
                            }, [
                              _createElementVNode("span", _hoisted_17, _toDisplayString(field.label), 1),
                              _createElementVNode("span", _hoisted_18, _toDisplayString(getMobileFieldValue(item, field.colDef)), 1)
                            ]))
                          }), 128))
                        ]))
                      : _createCommentVNode("", true)
                  ], 8, _hoisted_9))
                }), 128))
              ])),
          (_ctx.pagination && mobileTotalPages.value > 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                _createElementVNode("button", {
                  class: "mobile-pagination__btn",
                  disabled: mobilePage.value <= 1,
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (mobilePage.value--))
                }, [
                  _cache[7] || (_cache[7] = _createElementVNode("svg", {
                    viewBox: "0 0 24 24",
                    width: "16",
                    height: "16"
                  }, [
                    _createElementVNode("path", {
                      fill: "currentColor",
                      d: "M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z"
                    })
                  ], -1)),
                  _createTextVNode(" " + _toDisplayString(_unref(t)('common.previous')), 1)
                ], 8, _hoisted_20),
                _createElementVNode("span", _hoisted_21, _toDisplayString(mobilePage.value) + " / " + _toDisplayString(mobileTotalPages.value), 1),
                _createElementVNode("button", {
                  class: "mobile-pagination__btn",
                  disabled: mobilePage.value >= mobileTotalPages.value,
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (mobilePage.value++))
                }, [
                  _createTextVNode(_toDisplayString(_unref(t)('common.next')) + " ", 1),
                  _cache[8] || (_cache[8] = _createElementVNode("svg", {
                    viewBox: "0 0 24 24",
                    width: "16",
                    height: "16"
                  }, [
                    _createElementVNode("path", {
                      fill: "currentColor",
                      d: "M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"
                    })
                  ], -1))
                ], 8, _hoisted_22)
              ]))
            : _createCommentVNode("", true)
        ]))
  ]))
}
}

})